import {AfterViewChecked, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, Signal} from '@angular/core';
import {AudioRecorderService} from "@store/file-upload/audio-recorder.service";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {AsyncPipe, NgForOf, NgIf} from "@angular/common";
import {BehaviorSubject, EMPTY, of, switchMap} from "rxjs";
import {TranslocoModule} from "@ngneat/transloco";
import {ImageUpload} from "../../../../../../models/questions/ImageUpload";
import {AudioUploadService, IAudioUpload} from "@store/file-upload/audio-upload.service";
import {SurveyViewerService} from "../../../services/survey-viewer.service";
import {AbstractControl} from "@angular/forms";
import {IQuestionComponent} from "../../../../../../interfaces/IQuestionComponent";
import {MatTooltipModule} from "@angular/material/tooltip";
import {PipesModule} from "../../../../../../pipes/pipes.module";

@Component({
  selector: 'app-audio-recorder',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    NgIf,
    AsyncPipe,
    TranslocoModule,
    MatTooltipModule,
    NgForOf,
    PipesModule
  ],
  templateUrl: './audio-recorder.component.html',
  styleUrl: './audio-recorder.component.scss'
})
export class AudioRecorderComponent implements IQuestionComponent, AfterViewChecked {
  errorMessages: Record<string, {fr: string, en: string}> = {
    noRecordingValidator: {
      fr: 'L\'enregistrement audio n\'est pas confirmé. Merci d\'appuyer sur Stop ou de refaire l\'enregistrement au besoin.',
      en: 'We couldn\'t validate your audio. Please stop the recording, or redo it if needed, before continuing.'
    },
    noTextCanBeHeard: {
      fr: 'L\'enregistrement audio n\'est pas confirmé. Merci d\'appuyer sur Stop ou de refaire l\'enregistrement au besoin.',
      en: 'We couldn\'t validate your audio. Please stop the recording, or redo it if needed, before continuing.'
    },
    uploadFailed: {
      fr: 'L\'enregistrement audio n\'est pas confirmé. Merci d\'appuyer sur Stop ou de refaire l\'enregistrement au besoin.',
      en: 'We couldn\'t validate your audio. Please stop the recording, or redo it if needed, before continuing.'
    }
  }

  isRecording = new BehaviorSubject<boolean>(false);
  audioBlob?: Blob;
  audioBlobUrl = new BehaviorSubject<string>(undefined);
  @Input() isSmallScreen: boolean;
  @Input() activeLang: string;
  @Input() question: ImageUpload;

  questionFormControl: AbstractControl<any, any>;
  audioFormControl: AbstractControl<any, any>;
  commentsFormControl: AbstractControl<any, any>;

  audioUrl = '';

  hasError = new BehaviorSubject<Array<{fr: string, en: string}>>([]);
  isTouched = new BehaviorSubject<boolean>(false);

  constructor(
    private recorder: AudioRecorderService,
    public audioUploadService: AudioUploadService,
    public surveyViewerService: SurveyViewerService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngAfterViewChecked() {
    this.audioUploadService.uploadedAudio$[this.question.key] ??= new BehaviorSubject<IAudioUpload>(null);
    this.questionFormControl = this.surveyViewerService.activeSurvey$.value.form.get(this.question.key);
    this.audioFormControl = this.questionFormControl.get(this.question.rowsOrder[0]).get(this.question.audioControlName).get(this.question.statementsOrder[0]);
    this.commentsFormControl = this.questionFormControl.get(this.question.rowsOrder[0]).get(this.question.commentsControlName).get(this.question.statementsOrder[0]);
    this.isTouched.next(this.audioFormControl?.touched || false);
    if(this.audioFormControl?.value){
      this.audioUrl = this.audioFormControl.value;
      this.audioBlobUrl.next(this.audioFormControl.value)
      this.cdr.detectChanges();
    }
    this.audioUploadService.uploadedAudio$[this.question.key]
      .pipe(switchMap((d) => d && (d.url !== this.audioUrl) ? of(d) : EMPTY))
      .subscribe({
        next: (audioData) => {
          if (!audioData.transcription?.length) {
            this.hasError.next([this.errorMessages.noTextCanBeHeard]);
            this.audioBlob = undefined;
            this.audioBlobUrl.next(undefined);
            this.audioUploadService.uploadedAudio$[this.question.key].next(null);
            this.audioFormControl.setValue(null);
            this.audioFormControl.setErrors({noTextCanBeHeard: true});
            this.audioFormControl.markAsTouched();
            this.isTouched.next(true);
            this.audioFormControl.markAsDirty();
            this.audioFormControl.updateValueAndValidity({emitEvent: true});
            this.commentsFormControl.setValue('');
          }else{
            this.audioUrl = audioData?.url ?? '';
            this.audioFormControl.setValue(this.audioUrl);
            this.commentsFormControl.setValue(audioData.transcription)
            this.audioFormControl.setErrors(null);
            this.audioFormControl.markAsTouched();
            this.audioFormControl.updateValueAndValidity({emitEvent: true});
          }
        },
        error: () => {
          this.audioFormControl.setValue(null);
          this.audioFormControl.setErrors({uploadFailed: true});
          this.audioFormControl.markAsTouched();
          this.isTouched.next(true);
          this.audioFormControl.markAsDirty();
          this.audioFormControl.updateValueAndValidity({emitEvent: true});
          this.hasError.next([this.errorMessages.uploadFailed]);
        }
      });
    this.questionFormControl.get(this.question.rowsOrder[0]).get(this.question.audioControlName)
      .get(this.question.statementsOrder[0]).statusChanges.subscribe(value => {
      if (value === 'VALID') {
        this.surveyViewerService.activeSurvey$.value.form.get(this.question.key).markAsTouched()
        this.hasError.next([]);
      } else {
        // Only show errors if the control is touched or all form controls are marked as touched
        if (this.audioFormControl.touched || this.surveyViewerService.allFormControlsMarkedAsTouched$.value) {
          this.updateErrorMessages();
        }
      }
    })
    this.audioFormControl.statusChanges.subscribe(() => {
      this.isTouched.next(this.audioFormControl?.touched || false);
    });
    this.surveyViewerService.allFormControlsMarkedAsTouched$.subscribe(() => {
      this.updateErrorMessages();
    });
    this.surveyViewerService.activeSurvey$.subscribe(() => {this.updateErrorMessages()});
  }

  private updateErrorMessages() {
    // Only show errors if the control is touched or all form controls are marked as touched
    if (this.audioFormControl?.touched || this.surveyViewerService.allFormControlsMarkedAsTouched$.value) {
      if (this.audioFormControl?.errors) {
        const errors = Object.entries(this.audioFormControl.errors).map(([key, val]) => {
          if (val) {
            return this.errorMessages[key] || this.errorMessages.noRecordingValidator;
          }
        }).filter((msg): msg is {fr: string, en: string} => Boolean(msg));
        this.hasError.next(errors);
      } else if (this.audioFormControl?.valid) {
        this.hasError.next([]);
      }
    } else {
      // Don't show errors if the control hasn't been touched yet
      this.hasError.next([]);
    }
  }

  async start() {
    this.isRecording.next(true);
    await this.recorder.startRecording(this.question.key);
    this.audioFormControl.markAsUntouched();
    this.isTouched.next(false);
  }

  async stop() {
    this.isRecording.next(false);
    this.audioBlob = await this.recorder.stopRecording(this.question.key);
    this.audioBlobUrl.next(URL.createObjectURL(this.audioBlob));
    this.commentsFormControl.setValue('Invalid');
    this.upload();
  }

  eraseAudio() {
    this.audioBlob = undefined;
    this.audioBlobUrl.next(undefined);
    this.audioUploadService.uploadedAudio$[this.question.key]?.next(null);
    this.audioUploadService.isUploading$[this.question.key]?.next(false);
    this.audioFormControl.setValue(null);
    this.audioFormControl.setErrors({noRecordingValidator: true});
    this.audioFormControl.markAsTouched();
    this.isTouched.next(true);
    this.commentsFormControl.setValue('');
    this.audioFormControl.updateValueAndValidity({emitEvent: true});
    this.updateErrorMessages();
  }

  upload() {
    if (!this.audioBlob) return;
    this.hasError.next([]);
    const provisionalValue = this.audioBlobUrl.value || 'uploading';
    this.audioFormControl.setValue(provisionalValue);
    this.audioFormControl.markAsDirty();
    this.audioFormControl.setErrors(null);
    this.audioFormControl.markAsTouched();
    this.isTouched.next(true);
    this.questionFormControl.markAsTouched();
    this.audioFormControl.updateValueAndValidity({emitEvent: true});
    this.audioUploadService.uploadAudioFile(this.audioBlob, this.question.key, this.surveyViewerService.activeSurvey$.value.id);
  }

}
